/**
 * Is Cora good? Three lenses:
 *  1. Old vs new — during shadow, how often the new Cora agrees with the Relevance agent on the
 *     same candidate. This is the go/no-go for switching to live.
 *  2. Recruiters vs Cora — what happened to candidates after each verdict.
 *  3. Cora vs her own rules, and recruiters' 👎 — the runs worth a human look.
 */
import { Button } from '@finn-auto/design-system/atoms/button';
import type { CoraRunSummary, CoraStats } from '../../services/coraApi';
import { OUTCOME_LABEL, RECOMMENDATIONS, RECOMMENDATION_LABEL, fmtPct, fmtWhen } from './coraFormat';
import { RecommendationChip } from './ScreeningsTab';

function Kpi({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-sm border border-solid border-pearl p-4">
      <div className="text-xs uppercase text-zinc-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-zinc-900">{value}</div>
      <div className="mt-1 text-xs text-zinc-600">{hint}</div>
    </div>
  );
}

const OUTCOMES = ['advanced', 'archived', 'pending', 'unknown'] as const;

export default function QualityTab({ stats, runs, onOpen }: { stats: CoraStats; runs: CoraRunSummary[]; onOpen: (id: string) => void }) {
  const s = stats.shadow;
  const lookAt = runs.filter(
    (r) =>
      r.ruleCheckAgrees === false ||
      (r.baselineRecommendation && r.recommendation && r.baselineRecommendation !== r.recommendation) ||
      r.hasFeedback
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-3 md:grid-cols-4">
        <Kpi
          label="Agrees with old Cora"
          value={fmtPct(s.agreementPct)}
          hint={`${s.agree} of ${s.compared} compared · ${s.awaitingBaseline} waiting for the old verdict${s.noOldVerdict ? ` · ${s.noOldVerdict} without one` : ''}`}
        />
        <Kpi label="Own-rule disagreements" value={String(stats.ruleDisagreements)} hint="Verdict differs from what her criteria imply" />
        <Kpi label="Recruiter 👎" value={String(stats.feedbackDisagree)} hint="Screenings a recruiter marked as wrong" />
        <Kpi label="Salary mismatches" value={String(stats.salaryMismatch)} hint="Expectation outside the range, or missing" />
      </div>

      {Object.keys(s.pairs).length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold text-zinc-900">Old verdict → new verdict</h2>
          <ul className="grid gap-1 text-sm text-zinc-700 md:grid-cols-2">
            {Object.entries(s.pairs)
              .sort((a, b) => b[1] - a[1])
              .map(([pair, n]) => {
                const [from, to] = pair.split(' → ');
                return (
                  <li key={pair} className={from === to ? '' : 'font-medium text-orange'}>
                    {RECOMMENDATION_LABEL[from as keyof typeof RECOMMENDATION_LABEL] ?? from} →{' '}
                    {RECOMMENDATION_LABEL[to as keyof typeof RECOMMENDATION_LABEL] ?? to}: {n}
                  </li>
                );
              })}
          </ul>
        </section>
      )}

      <section>
        <h2 className="mb-2 text-sm font-semibold text-zinc-900">What recruiters did after each verdict</h2>
        <div className="overflow-x-auto rounded-sm border border-solid border-pearl">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-solid border-pearl text-xs uppercase text-zinc-500">
              <tr>
                <th className="px-3 py-2">Verdict</th>
                {OUTCOMES.map((o) => <th key={o} className="px-3 py-2">{OUTCOME_LABEL[o] === '—' ? 'Not checked yet' : OUTCOME_LABEL[o]}</th>)}
              </tr>
            </thead>
            <tbody>
              {RECOMMENDATIONS.map((rec) => (
                <tr key={rec} className="border-b border-solid border-pearl last:border-0">
                  <td className="px-3 py-2"><RecommendationChip value={rec} /> <span className="text-zinc-500">({stats.recommendations[rec]})</span></td>
                  {OUTCOMES.map((o) => <td key={o} className="px-3 py-2 text-zinc-700">{stats.outcomes[rec][o]}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-zinc-900">Worth a look ({lookAt.length})</h2>
        {lookAt.length === 0 ? (
          <p className="text-sm text-zinc-600">Nothing flagged in this period.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {lookAt.slice(0, 50).map((r) => (
              <li key={r.id}>
                <Button variant="ghost" className="flex h-auto w-full flex-wrap items-center justify-start gap-2 p-2 text-left text-sm font-normal" onClick={() => onOpen(r.id)}>
                  <span className="font-medium text-zinc-900">{r.candidateName}</span>
                  <span className="text-zinc-500">{r.positionTitle}</span>
                  <RecommendationChip value={r.recommendation} />
                  {r.baselineRecommendation && r.baselineRecommendation !== r.recommendation && (
                    <span className="text-xs text-orange">old: {RECOMMENDATION_LABEL[r.baselineRecommendation]}</span>
                  )}
                  {r.ruleCheckAgrees === false && <span className="text-xs text-orange">rule check</span>}
                  {r.hasFeedback && <span className="text-xs text-zinc-600">feedback</span>}
                  <span className="ml-auto text-xs text-zinc-500">{fmtWhen(r.finishedAt || r.startedAt)}</span>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
